import type { ComponentPropsWithoutRef } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_ABOUT_ANCHOR,
} from '@/constants/anchors';
import { COMPANY_SNOWFLAKE } from '@/constants/feedbackSections';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import {
  SnowflakeChart,
  RiskRewardWrapper,
  SummarySection,
  OverviewSectionHeader,
  SnowflakeTitle,
  SnowflakeCaption,
  SnowflakeDescription,
} from './styled';
import RiskRewardLoadingSkeleton from '../RiskRewardLoadingSkeleton';
import { Button } from '@simplywallst/ui-core/next';
import loadable from '@loadable/component';
import SnowflakeChartWrapper from '../SnowflakeChartWrapper';
import SnowflakeTable from '../SnowflakeTable';
import RiskReward from '../RiskReward';
import RiskSummaryList from '../RiskSummaryList';
import { useCompanyCanonicalUrl, useSummaryV2 } from '@/hooks/useCompany';
import RiskRewardHeading from '../RiskRewardHeading';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import { isValidSection } from '@/pages/CompanyReport/useSection';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted/useCompanyStatementsAPIFormatted';
import {
  getPassRewardList,
  getRiskList,
  getRiskRewardFragments,
  getSectionChecks,
} from '../../utils';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import useUser from '@/hooks/useUser';
import { useRegistrationModal } from '@/context/RegistrationModalContext';

const RiskCheckModal = loadable(() => import('../RiskCheckModal'));

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));

const PortfolioOnboardingDesktop = loadable(
  () => import('../../../PortfolioOnboarding'),
  { resolveComponent: (c) => c.PortfolioOnboardingDesktop }
);

const PortfolioOnboardingMobile = loadable(
  () => import('../../../PortfolioOnboarding'),
  { resolveComponent: (c) => c.PortfolioOnboardingMobile }
);

const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

type Props = {
  handleShowAboutCompany: (value: boolean) => void;
};

export function Summary({ handleShowAboutCompany }: Props) {
  const { data: user } = useUser();
  const { shouldShowRego, showRegoModal } = useRegistrationModal();

  const {
    isFund,
    description,
    score: snowflakePoints,
    uniqueSymbol,
    tickerSymbol,
    name: companyName,
    status: summaryQueryStatus,
    shortName,
  } = useSummaryV2();
  const canonicalURL = useCompanyCanonicalUrl();
  const { trackEvent } = useTracking({ type: 'track', subject: 'summary' });
  const [openRisksModal, setOpenRiskModal] = useState(false);
  const { loadSection } = useVisibleSectionsContext();
  const { data: statements, status: statementApiStatus } =
    useCompanyStatementsAPIFormatted({
      canonicalURL,
    });
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const {
    IsProfitable: isProfitable,
    IsProfitableLongTerm: isProfitableLongTerm,
    IsBank: isBank,
  } = statements || {};

  const isLossMaking =
    isProfitable?.value === false && isProfitableLongTerm?.value === false;

  const isFinancialCompany = isBank?.value === true;

  const sectionChecks = getSectionChecks(
    isFinancialCompany,
    isLossMaking,
    statements
  );

  const sentence = statements?.sentence;
  const riskList = getRiskList(statements);
  const passRewardsList = getPassRewardList(statements);
  const scroller = useScrollIntoView();

  const t = useOverviewTranslation();

  useEffect(() => {
    trackEvent({
      action: 'landed',
    });
  }, [trackEvent]);

  const companyDescriptions = useMemo(
    () => [
      t('snowflake.tooltip.description0'),
      t('snowflake.tooltip.description1'),
      t('snowflake.tooltip.description2'),
      t('snowflake.tooltip.description3'),
      t('snowflake.tooltip.description4'),
    ],
    []
  );

  const handleSnowflakeClick = useCallback<
    NonNullable<ComponentPropsWithoutRef<typeof Snowflake>['handleClick']>
  >(
    (route) => {
      if (isValidSection(route)) {
        loadSection(route);
      } else {
        console.warn('attempted to redirect to an invalid section:', route);
      }
    },
    [loadSection]
  );

  return (
    <>
      {user.loggedIn && !isMobileScreen && <PortfolioOnboardingDesktop />}
      <SummarySection
        data-cy-id="company-summary"
        className={isMobileScreen ? 'relative z-1 ' : ''}
      >
        {user.loggedIn && isMobileScreen && <PortfolioOnboardingMobile />}

        <div className="grid grid-cols-[1fr_1fr] grid-rows-[auto_1fr] gap-x1 sm:grid-cols-[minmax(0,1fr)_200px] md:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <OverviewSectionHeader>
              {summaryQueryStatus === 'success' ? (
                t('about.header', { shortName, tickerSymbol })
              ) : (
                <div className="bones max-w-[30ch]">&nbsp;</div>
              )}
            </OverviewSectionHeader>
            <p className="m-0" data-cy-id="company-summary-desc">
              <span className={description ? '' : 'opacity-50'}>
                {summaryQueryStatus === 'success' ? (
                  description || `${t('noDescription')}.`
                ) : (
                  <span className="bones max-w-[40ch]">&nbsp;</span>
                )}
              </span>{' '}
              {summaryQueryStatus === 'success' && (
                <a
                  href={`#${COMPANY_ABOUT_ANCHOR}`}
                  className="inline-grid grid-flow-col items-center whitespace-nowrap text-brand-01 no-underline"
                  data-cy-id="company-about-button"
                  onClick={(e) => {
                    e.preventDefault();
                    trackEvent({
                      modifier: 'about-company-read-more',
                      action: 'click',
                    });
                    if (shouldShowRego) {
                      showRegoModal();
                      return;
                    }
                    scroller(`#${COMPANY_ABOUT_ANCHOR}`);
                    handleShowAboutCompany(true);
                  }}
                >
                  {t('about.more')}
                  <ChevronRightSmallSolid className="-ml-0.5 fill-current" />
                </a>
              )}
            </p>
          </div>
          <div className="row-span-1 grid sm:row-span-2">
            <div>
              <SnowflakeChartWrapper
                id="overview-summary-snowflake"
                points={snowflakePoints || [0, 0, 0, 0, 0]}
                companyForFeedback={uniqueSymbol}
                feedbackLabel={COMPANY_SNOWFLAKE}
                chart={
                  <SnowflakeChart data-cy-id="overview-summary-snowflake">
                    <Snowflake
                      points={snowflakePoints}
                      detailedPoints={sectionChecks}
                      tooltip={true}
                      routes={routes}
                      handleClick={handleSnowflakeClick}
                      disabled={isFund}
                      type={SnowflakeType.COMPANY}
                      companyDescriptions={companyDescriptions}
                    />
                    <SnowflakeDescription>
                      <SnowflakeTitle>{t('snowflake')}</SnowflakeTitle>
                      {sentence && !isFund && (
                        <SnowflakeCaption data-cy-id="company-fundamentals-desc">
                          {sentence}
                        </SnowflakeCaption>
                      )}
                    </SnowflakeDescription>
                  </SnowflakeChart>
                }
                table={
                  <SnowflakeChart>
                    <SnowflakeTable
                      points={snowflakePoints || [0, 0, 0, 0, 0]}
                      tickerSymbol={tickerSymbol}
                    />
                  </SnowflakeChart>
                }
              />
            </div>
          </div>
          <div className="col-span-2 grid sm:col-span-1">
            <RiskRewardWrapper data-cy-id="risk-reward-wrapper">
              {statementApiStatus === 'success' ? (
                <>
                  {!isFund && passRewardsList.length > 0 && (
                    <>
                      <RiskRewardHeading>{t('rewards')}</RiskRewardHeading>
                      {passRewardsList.map((reward, i) => {
                        const { fragment } = getRiskRewardFragments(
                          reward.name,
                          ''
                        );
                        return (
                          <RiskReward
                            key={reward.name}
                            name={reward.name}
                            index={i}
                            hash={fragment}
                            description={reward.description}
                          />
                        );
                      })}
                    </>
                  )}
                  {!isFund && (
                    <>
                      <RiskRewardHeading>{t('risk')}</RiskRewardHeading>
                      <RiskSummaryList
                        risks={riskList}
                        tickerSymbol={tickerSymbol}
                      />
                      <Button
                        className="max-sm:w-full"
                        onClick={() => {
                          trackEvent({
                            modifier: 'see-all-risk-checks',
                            action: 'click',
                          });
                          if (shouldShowRego) {
                            showRegoModal();
                            return;
                          }
                          setOpenRiskModal(true);
                        }}
                        variant="secondary"
                        size="medium"
                        data-cy-id="summary-risk-modal-button"
                      >
                        {t('seeAllRisks')}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <RiskRewardHeading>{t('rewards')}</RiskRewardHeading>
                  <RiskRewardLoadingSkeleton data-cy-id="reward-loading-skeleton" />
                  <RiskRewardHeading>{t('risk')}</RiskRewardHeading>
                  <RiskRewardLoadingSkeleton data-cy-id="risk-loading-skeleton" />
                </>
              )}
              {openRisksModal && (
                <RiskCheckModal
                  companyShortName={companyName}
                  companyTickerSymbol={tickerSymbol}
                  risks={riskList}
                  onClose={() => setOpenRiskModal(false)}
                />
              )}
            </RiskRewardWrapper>
          </div>
        </div>
      </SummarySection>
    </>
  );
}
